import { Box, Collapse, Divider, IconButton, Skeleton, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useListState } from '@shared/hooks/useListState';
import {
    getMatchedTestResultByIdRequest,
    getMatchedTestResultByIdReset,
    getMatchedTestResultsRequest,
    getMatchedTestResultsReset,
} from '@pages/testruns/store';
import { AutoLinkPatternField } from '../types';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectGetMatchedTestResultByIdStatus,
    selectGetMatchedTestResultsStatus,
    selectMatchedTestResult,
    selectMatchedTestResults,
    selectMatchedTestResultsCount,
} from '@pages/testruns/store/selectors';
import OverflowTooltip from '@shared/components/ui/OverflowTooltip';
import TestResultChip from '@pages/testruns/components/TestResultChip';
import ErrorTraceBlock from '@shared/components/others/ErrorTraceBlock/ErrorTraceBlock';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import { useTestrunsChunk } from '@pages/testruns/hooks/useTestrunsChunk';
import LoadingPlaceholder from '@shared/components/ui/LoadingPlaceHolder';
import { RequestStatusEnum } from '@shared/types';
import UtmsPagination from '@shared/components/ui/UtmsPagination';

interface DefectMatchedResultsListProps {
    fieldName: AutoLinkPatternField | null;
    pattern: string;
}

const DefectMatchedResultsList = (props: DefectMatchedResultsListProps) => (
    <ComponentWithChunks
        featuresArray={['testRuns']}
        hooksArray={[useTestrunsChunk]}
        Component={DefectMatchedResultsListComponent}
        {...props}
    />
);

const DefectMatchedResultsListComponent: FC<DefectMatchedResultsListProps> = ({ pattern, fieldName }) => {
    const { t } = useTranslation();
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
    const [details, setDetails] = useState<Record<number, { errorMessage: string; stackTrace: string }>>({});
    const matchedTestResults = useSelector(selectMatchedTestResults);
    const matchedTestResult = useSelector(selectMatchedTestResult);
    const matchedTestResultsCount = useSelector(selectMatchedTestResultsCount);
    const getMatchedTestResultsStatus = useSelector(selectGetMatchedTestResultsStatus);
    const getMatchedTestResultByIdStatus = useSelector(selectGetMatchedTestResultByIdStatus);
    const isListLoading = getMatchedTestResultsStatus === RequestStatusEnum.Loading;
    const { pagination, setPagination } = useListState('matchedResultsList', false, getMatchedTestResultsRequest, {
        disableUrlParams: true,
        ...(fieldName ? { [fieldName]: pattern } : {}),
    });
    const dispatch = useDispatch();

    const handleToggle = (testresult: any) => {
        const { pk, is_handtest } = testresult;

        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(pk)) {
                next.delete(pk);
            } else {
                next.add(pk);
                if (!details[pk]) {
                    //@ts-ignore
                    dispatch(getMatchedTestResultByIdRequest({ testresultId: pk, isHandtest: is_handtest }));
                }
            }
            return next;
        });
    };

    useEffect(() => {
        return () => {
            dispatch(getMatchedTestResultsReset());
            dispatch(getMatchedTestResultByIdReset());
        };
    }, [dispatch]);

    useEffect(() => {
        if (matchedTestResult) {
            setDetails((prev) => ({
                ...prev,
                [matchedTestResult.pk]: {
                    errorMessage: matchedTestResult.error_message,
                    stackTrace: matchedTestResult.stack_trace,
                },
            }));
        }
    }, [matchedTestResult]);
    return (
        <LoadingPlaceholder
            isLoading={isListLoading}
            skeleton={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: '8px 16px' }}>
                    {Array.from({ length: 10 }).map((_, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Skeleton animation="wave" width={40} />
                                <Skeleton animation="wave" width={200} />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Skeleton animation="wave" variant="rounded" width={60} height={24} />
                                <Skeleton animation="wave" variant="circular" width={28} height={28} />
                            </Box>
                        </Box>
                    ))}
                </Box>
            }
        >
            {!matchedTestResults.length ? (
                <Typography sx={{ color: 'text.secondary' }}>{t('no_matched_results')}</Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: 640 }}>
                    <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                        {matchedTestResults.map((result: any) => (
                            <Fragment key={result.pk}>
                                <Box sx={{ p: '8px 16px' }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Typography>{result.pk}</Typography>
                                            <OverflowTooltip title={result.testcase.name}>
                                                {result.testcase.name}
                                            </OverflowTooltip>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                            <TestResultChip result={result.result} />
                                            <IconButton
                                                size="small"
                                                onClick={() => handleToggle(result)}
                                                sx={{
                                                    transform: expandedIds.has(result.pk)
                                                        ? 'rotate(180deg)'
                                                        : 'rotate(0deg)',
                                                    transition: 'transform 0.2s',
                                                }}
                                            >
                                                <ExpandMoreIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    <Collapse in={expandedIds.has(result.pk)}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1,
                                                pt: 1,
                                            }}
                                        >
                                            <LoadingPlaceholder
                                                isLoading={
                                                    getMatchedTestResultByIdStatus === RequestStatusEnum.Loading &&
                                                    !details[result.pk]
                                                }
                                                skeleton={
                                                    <>
                                                        <Skeleton
                                                            animation="wave"
                                                            variant="rounded"
                                                            width="100%"
                                                            height={80}
                                                        />
                                                        <Skeleton
                                                            animation="wave"
                                                            variant="rounded"
                                                            width="100%"
                                                            height={200}
                                                        />
                                                    </>
                                                }
                                            >
                                                {details[result.pk]?.errorMessage ? (
                                                    <ErrorTraceBlock text={details[result.pk]?.errorMessage} />
                                                ) : (
                                                    <Typography color="text.disabled">
                                                        {t('no_error_message')}
                                                    </Typography>
                                                )}
                                                {details[result.pk]?.stackTrace ? (
                                                    <ErrorTraceBlock text={details[result.pk]?.stackTrace} />
                                                ) : (
                                                    <Typography color="text.disabled">{t('no_stack_trace')}</Typography>
                                                )}
                                            </LoadingPlaceholder>
                                        </Box>
                                    </Collapse>
                                </Box>
                                <Divider />
                            </Fragment>
                        ))}
                    </Box>
                    <UtmsPagination
                        pagination={pagination}
                        handleChangePagination={setPagination}
                        count={matchedTestResultsCount}
                    />
                </Box>
            )}
        </LoadingPlaceholder>
    );
};

export default DefectMatchedResultsList;
